import React, {useCallback, useEffect, useRef, useState} from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Input,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import SignatureCanvas from "react-signature-canvas";
import {RiDeleteBin5Line} from "react-icons/ri";
import {FiCheckCircle, FiDownload, FiUploadCloud, FiFile, FiX} from "react-icons/fi";
import axios from "axios";
import {url} from "../crudRequests";
import {uploadSignedConsent} from "../components/Consents/consentRequests";

// Standalone consent signing page reached from the emailed link.
//
// Deliberately OUTSIDE the authenticated Layout: a patient asked to sign
// before their first visit may have no portal account at all. Authorization is
// the capability in `?params=` — the server checks it on every call and the
// page never sends a patient id, tenant or carrier of its own.
//
// What crosses the wire is the signature image and the two consent decisions.
// The server builds the official TELUS form from them; nothing here can shape
// the resulting document.
export default function ConsentForm() {
  const toast = useToast();
  const [params] = useState(() => {
    const raw = window.location.href.split("?params=")[1] || "";
    return decodeURIComponent(raw);
  });

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [alreadySigned, setAlreadySigned] = useState(false);

  const [formUrl, setFormUrl] = useState(null);
  const formUrlRef = useRef(null);

  const [personalInfoConsent, setPersonalInfoConsent] = useState(false);
  const [benefitAssignment, setBenefitAssignment] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [hasStrokes, setHasStrokes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Two ways to complete this, both ending in the same record: sign right here,
  // or take the form away, sign it wherever you like, and send it back. The
  // second exists because plenty of people would rather use the PDF tool they
  // already trust than draw on a canvas — and because without it the only
  // route was to email the clinic and wait for someone to key it in.
  const [mode, setMode] = useState("sign");
  const [uploadFile, setUploadFile] = useState(null);
  const uploadInputRef = useRef(null);

  const signatureRef = useRef(null);

  const load = useCallback(async () => {
    if (!params) {
      setLoadError("This link is not valid.");
      setLoading(false);
      return;
    }
    try {
      const response = await axios.post(`${url}/public/consent/resolve`, {params});
      setInfo(response.data);
      setSignerName((previous) => previous || response.data?.patientName || "");
    } catch (error) {
      setAlreadySigned(error?.response?.data?.alreadySigned === true);
      setLoadError(
        error?.response?.data?.error || "We could not open this consent form."
      );
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  // Fetch the pre-filled PDF as a blob: the endpoint is a POST carrying the
  // capability, so it cannot be used as a plain iframe src.
  useEffect(() => {
    let cancelled = false;
    if (!info || !params) return undefined;
    (async () => {
      try {
        const response = await axios.post(
          `${url}/public/consent/form`,
          {params},
          {responseType: "blob"}
        );
        if (cancelled) return;
        const blobUrl = window.URL.createObjectURL(
          new Blob([response.data], {type: "application/pdf"})
        );
        formUrlRef.current = blobUrl;
        setFormUrl(blobUrl);
      } catch {
        // Non-fatal: the patient can still sign without the inline preview.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [info, params]);

  useEffect(
    () => () => {
      if (formUrlRef.current) window.URL.revokeObjectURL(formUrlRef.current);
    },
    []
  );

  const baseValid = personalInfoConsent && signerName.trim().length > 1 && !saving;
  const canSubmit =
    mode === "sign" ? baseValid && hasStrokes : baseValid && Boolean(uploadFile);

  // Download the fillable form so it can be completed in whatever PDF tool the
  // patient already uses.
  function downloadForm() {
    if (!formUrl) return;
    const link = document.createElement("a");
    link.href = formUrl;
    link.download = "TELUS-eClaims-consent-form.pdf";
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSaving(true);
    try {
      if (mode === "sign") {
        await axios.post(`${url}/public/consent/sign`, {
          params,
          signatureImage: signatureRef.current?.toDataURL("image/png"),
          signerName: signerName.trim(),
          personalInfoConsent,
          benefitAssignment,
        });
      } else {
        await uploadSignedConsent(params, {
          file: uploadFile,
          signerName: signerName.trim(),
          personalInfoConsent,
          benefitAssignment,
        });
      }
      setDone(true);
    } catch (error) {
      toast({
        title:
          mode === "sign"
            ? "We couldn't save your signature"
            : "We couldn't save your document",
        description:
          error?.response?.data?.error ||
          error?.message ||
          "Please try again.",
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Flex align="center" justify="center" minH="100vh" bg="gray.50">
        <Spinner size="md" mr={3} />
        <Text color="gray.600">Loading your consent form…</Text>
      </Flex>
    );
  }

  if (done) {
    return (
      <Flex align="center" justify="center" minH="100vh" bg="gray.50" px={4}>
        <Box
          bg="white"
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          p={10}
          maxW="520px"
          textAlign="center"
        >
          <Flex justify="center" mb={3} color="green.500" fontSize="40px">
            <FiCheckCircle />
          </Flex>
          <Heading fontSize="xl" mb={2} color="#1a1f36">
            Thank you — your consent is signed
          </Heading>
          <Text fontSize="sm" color="#6b7280">
            {info?.clinicName || "Your clinic"} has been notified and can now
            submit insurance claims on your behalf. You can close this page.
          </Text>
        </Box>
      </Flex>
    );
  }

  if (loadError) {
    return (
      <Flex align="center" justify="center" minH="100vh" bg="gray.50" px={4}>
        <Box
          bg="white"
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          p={10}
          maxW="520px"
          textAlign="center"
        >
          <Heading fontSize="lg" mb={2} color="#1a1f36">
            {alreadySigned ? "Already signed" : "This link can't be opened"}
          </Heading>
          <Text fontSize="sm" color="#6b7280">
            {loadError}
          </Text>
        </Box>
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={{base: 5, md: 10}} px={{base: 3, md: 6}}>
      <Box maxW="900px" mx="auto">
        <Box mb={5}>
          <Heading fontSize="xl" fontWeight="600" color="#1a1f36">
            Electronic transmission authorization
          </Heading>
          <Text fontSize="13px" color="#6b7280" mt={1}>
            {info?.clinicName
              ? `${info.clinicName} needs your authorization to submit insurance claims electronically on your behalf.`
              : "Your clinic needs your authorization to submit insurance claims electronically on your behalf."}
          </Text>
        </Box>

        <Text fontSize="12px" color="#6b7280" mb={2}>
          Please read the form below. You do not fill anything in on the
          document itself — use the boxes underneath it to agree and sign.
        </Text>
        <Box
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          overflow="hidden"
          bg="white"
          mb={5}
        >
          {formUrl ? (
            <Box
              as="iframe"
              src={formUrl}
              title="Electronic transmission authorization and consent form"
              width="100%"
              height="520px"
              border="none"
            />
          ) : (
            <Flex align="center" justify="center" py={10} px={4}>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                Loading the form…
              </Text>
            </Flex>
          )}
        </Box>

        <Box
          bg="white"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          p={{base: 4, md: 6}}
        >
          <VStack align="stretch" spacing={5}>
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={2}>
                What you are agreeing to
              </Text>
              <VStack align="stretch" spacing={3}>
                <Checkbox
                  isChecked={personalInfoConsent}
                  onChange={(event) =>
                    setPersonalInfoConsent(event.target.checked)
                  }
                >
                  <Text fontSize="sm">
                    I authorize the collection, use and exchange of my personal
                    information with my insurer for the administration of my
                    claims.
                  </Text>
                </Checkbox>
                <Checkbox
                  isChecked={benefitAssignment}
                  onChange={(event) => setBenefitAssignment(event.target.checked)}
                >
                  <Text fontSize="sm">
                    I assign the benefits payable for my claims to my healthcare
                    provider, so the insurer pays them directly.
                  </Text>
                </Checkbox>
              </VStack>
              {!personalInfoConsent && (
                <Text fontSize="xs" color="orange.600" mt={2}>
                  The first authorization is required — without it claims cannot
                  be submitted electronically.
                </Text>
              )}
              {personalInfoConsent && !benefitAssignment && (
                <Alert status="info" borderRadius="md" fontSize="xs" mt={3}>
                  <AlertIcon />
                  Without the second authorization your insurer will reimburse
                  you directly, and you will settle the visit with the clinic
                  yourself.
                </Alert>
              )}
            </Box>

            <Box borderTopWidth="1px" borderColor="gray.100" pt={4}>
              <Text fontSize="sm" fontWeight="semibold" mb={2}>
                How would you like to sign?
              </Text>
              <Flex gap={2} wrap="wrap">
                <Button
                  size="sm"
                  variant={mode === "sign" ? "solid" : "outline"}
                  colorScheme={mode === "sign" ? "blue" : "gray"}
                  onClick={() => setMode("sign")}
                >
                  Sign here
                </Button>
                <Button
                  size="sm"
                  variant={mode === "upload" ? "solid" : "outline"}
                  colorScheme={mode === "upload" ? "blue" : "gray"}
                  onClick={() => setMode("upload")}
                >
                  Sign it myself and send it back
                </Button>
              </Flex>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>
                Printed name of the plan member
              </Text>
              <Input
                size="sm"
                bg="white"
                maxW="360px"
                value={signerName}
                onChange={(event) => setSignerName(event.target.value)}
                placeholder="Full name"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                If the plan is held by someone else (for example a parent or
                spouse), enter the plan member&apos;s name.
              </Text>
            </Box>

            {mode === "sign" ? (
              <Box>
                <Flex align="center" justify="space-between" mb={1} maxW="480px">
                  <Text fontSize="sm" fontWeight="semibold">
                    Signature
                  </Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={<RiDeleteBin5Line />}
                    onClick={() => {
                      signatureRef.current?.clear();
                      setHasStrokes(false);
                    }}
                  >
                    Clear
                  </Button>
                </Flex>
                <Box
                  borderWidth="1px"
                  borderColor="gray.300"
                  borderRadius="md"
                  bg="white"
                  overflow="hidden"
                  maxW="480px"
                >
                  <SignatureCanvas
                    ref={signatureRef}
                    penColor="#111827"
                    maxWidth={2.5}
                    onEnd={() => setHasStrokes(true)}
                    canvasProps={{height: 160, width: 470}}
                  />
                </Box>
                {!hasStrokes && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Sign above using your mouse or finger.
                  </Text>
                )}
              </Box>
            ) : (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={1}>
                  Your signed form
                </Text>
                <Text fontSize="xs" color="gray.600" mb={3}>
                  Download the form, sign it however you prefer, then send it
                  back here. Your clinic receives it right away.
                </Text>
                <Flex gap={2} wrap="wrap" align="center">
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<FiDownload />}
                    onClick={downloadForm}
                    isDisabled={!formUrl}
                  >
                    Download the form
                  </Button>
                  <input
                    ref={uploadInputRef}
                    type="file"
                    accept="application/pdf"
                    style={{display: "none"}}
                    onChange={(event) => {
                      setUploadFile(event.target.files?.[0] || null);
                      // Reset so choosing the same file again still fires.
                      event.target.value = "";
                    }}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<FiUploadCloud />}
                    onClick={() => uploadInputRef.current?.click()}
                  >
                    Choose your signed PDF
                  </Button>
                </Flex>
                {uploadFile && (
                  <Flex
                    mt={3}
                    align="center"
                    gap={2}
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="full"
                    px={3}
                    py={1}
                    w="fit-content"
                    maxW="100%"
                  >
                    <FiFile color="#9ca3af" />
                    <Text fontSize="xs" noOfLines={1}>
                      {uploadFile.name}
                    </Text>
                    <Text fontSize="xs" color="gray.400">
                      {(uploadFile.size / 1024).toFixed(0)} KB
                    </Text>
                    <Box
                      as="button"
                      type="button"
                      aria-label="Remove file"
                      color="gray.400"
                      _hover={{color: "gray.600"}}
                      onClick={() => setUploadFile(null)}
                    >
                      <FiX />
                    </Box>
                  </Flex>
                )}
                {!uploadFile && (
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    PDF only.
                  </Text>
                )}
              </Box>
            )}

            <Box>
              <Button
                colorScheme="blue"
                isDisabled={!canSubmit}
                isLoading={saving}
                loadingText="Saving…"
                onClick={handleSubmit}
              >
                {mode === "sign" ? "Sign and submit" : "Send my signed form"}
              </Button>
            </Box>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
}
